import React from 'react';
import { useAppSelector } from '../../hooks/redux';
import Button from '../UI/Button';
import FormSection from '../UI/FormSection';

interface SecuritySectionProps {
  className?: string;
}

const SecuritySection: React.FC<SecuritySectionProps> = ({ className = '' }) => {
  const { user } = useAppSelector((state) => state.auth);

  const handleEnable2FA = () => {
    // TODO: Implement 2FA enable/disable
    console.log('Toggle 2FA');
  };

  const handleManagePasskeys = () => {
    // TODO: Implement passkey management
    console.log('Manage passkeys');
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <FormSection title="Security Settings" className={className}>
      <div className="space-y-4">
        {/* Two-Factor Authentication */}
        <div className="p-4 border border-[var(--color-neutral-border)] rounded-lg bg-[var(--color-default-background)]">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold mb-2 text-[var(--color-default-font)]">
                Two-Factor Authentication
              </h3>
              <p className="text-sm text-[var(--color-subtext-color)] mb-3">
                {user?.authentication?.twoFactorEnabled
                  ? 'Two-factor authentication is enabled. Your account has an extra layer of security.'
                  : 'Two-factor authentication is not enabled. Add an extra layer of security to your account.'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEnable2FA}
            >
              {user?.authentication?.twoFactorEnabled ? 'Disable' : 'Enable'} 2FA
            </Button>
          </div>
        </div>

        {/* Passkeys */}
        <div className="p-4 border border-[var(--color-neutral-border)] rounded-lg bg-[var(--color-default-background)]">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold mb-2 text-[var(--color-default-font)]">
                Passkeys
              </h3>
              <p className="text-sm text-[var(--color-subtext-color)] mb-3">
                {user?.authentication?.passkeys?.length || 0} passkey(s) registered. 
                Passkeys provide a secure, passwordless way to sign in.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManagePasskeys}
            >
              Manage Passkeys
            </Button>
          </div>
        </div>

        {/* Access History */}
        <div className="p-4 border border-[var(--color-neutral-border)] rounded-lg bg-[var(--color-default-background)]">
          <h3 className="font-semibold mb-3 text-[var(--color-default-font)]">
            Recent Access History
          </h3>
          {user?.authentication?.accessHistory && user.authentication.accessHistory.length > 0 ? (
            <div className="space-y-3">
              {user.authentication.accessHistory
                .slice(-5)
                .reverse()
                .map((entry: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-start justify-between py-2 border-b border-[var(--color-neutral-border)] last:border-0"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[var(--color-default-font)]">
                        {entry.action || 'Access'}
                      </p>
                      <p className="text-xs text-[var(--color-subtext-color)] mt-1">
                        {formatDate(entry.timestamp)}
                      </p>
                      {entry.ipAddress && (
                        <p className="text-xs text-[var(--color-subtext-color)]">
                          IP: {entry.ipAddress}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-subtext-color)] italic">
              No access history available
            </p>
          )}
        </div>
      </div>
    </FormSection>
  );
};

export default SecuritySection;

